import { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  Send, 
  MessageCircle,
  Instagram,
  Facebook
} from 'lucide-react';

const contactInfo = [
  {
    icon: Phone,
    title: 'Telefone / WhatsApp',
    content: '(47) 99767-9994',
    link: 'https://wa.me/5547997679994',
    linkText: 'Falar no WhatsApp',
  },
  {
    icon: Mail,
    title: 'E-mail',
    content: 'contato@fort3d.com.br',
    link: 'mailto:contato@fort3d.com.br',
    linkText: 'Enviar e-mail',
  },
  {
    icon: MapPin,
    title: 'Endereço',
    content: 'São Paulo, SP - Brasil',
    link: null,
    linkText: null,
  },
  {
    icon: Clock,
    title: 'Horário de Atendimento',
    content: 'Segunda a Sexta: 9h às 18h',
    link: null,
    linkText: null,
  },
];

const faq = [
  {
    question: 'Qual o prazo de entrega?',
    answer: 'O prazo varia de acordo com o produto e local de entrega. Produtos em estoque são enviados em 24h. Produtos sob encomenda levam de 2 a 5 dias úteis para produção.',
  },
  {
    question: 'Vocês fazem produtos personalizados?',
    answer: 'Sim! Trabalhamos com projetos personalizados. Envie sua ideia ou arquivo 3D que faremos um orçamento sem compromisso.',
  },
  {
    question: 'Quais materiais vocês utilizam?',
    answer: 'Utilizamos principalmente PLA Premium, mas também trabalhamos com ABS, PETG, TPU e outros materiais sob demanda.',
  },
  {
    question: 'Qual a política de trocas?',
    answer: 'Oferecemos garantia de 30 dias contra defeitos de fabricação. Produtos personalizados não têm devolução, mas garantimos a qualidade antes do envio.',
  },
];

export function Contato() {
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    assunto: '',
    mensagem: '',
  });
  const [enviado, setEnviado] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Simulação de envio
    setEnviado(true);
    setTimeout(() => {
      setEnviado(false);
      setFormData({ nome: '', email: '', assunto: '', mensagem: '' });
    }, 3000);
  };

  return (
    <div className="min-h-screen bg-gray-950">
      {/* Hero */}
      <section className="relative py-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 via-transparent to-cyan-500/5" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="inline-block px-4 py-2 rounded-full bg-orange-500/10 text-orange-400 text-sm font-medium mb-6">
              Fale Conosco
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              Entre em <span className="text-orange-500">Contato</span>
            </h1>
            <p className="text-gray-400 text-lg">
              Estamos aqui para ajudar. Entre em contato para tirar dúvidas, 
              fazer orçamentos ou apenas bater um papo sobre impressão 3D.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 rounded-2xl bg-gray-900/50 border border-white/5 text-center"
              >
                <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-orange-500/10 flex items-center justify-center">
                  <item.icon className="w-6 h-6 text-orange-500" />
                </div>
                <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                <p className="text-gray-400 text-sm mb-3">{item.content}</p>
                {item.link && (
                  <a
                    href={item.link}
                    target={item.link.startsWith('http') ? '_blank' : undefined}
                    rel={item.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                    className="text-orange-400 text-sm hover:text-orange-300 transition-colors"
                  >
                    {item.linkText} →
                  </a>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & WhatsApp CTA */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gray-900/50 rounded-2xl p-8 border border-white/5"
            >
              <h2 className="text-2xl font-bold text-white mb-6">
                Envie uma Mensagem
              </h2>
              
              {enviado ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
                    <Send className="w-8 h-8 text-green-500" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">Mensagem Enviada!</h3>
                  <p className="text-gray-400">Responderemos o mais breve possível.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <Input
                    label="Nome"
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    placeholder="Seu nome"
                    required
                  />
                  <Input
                    label="E-mail"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="seu@email.com"
                    required
                  />
                  <Input
                    label="Assunto"
                    value={formData.assunto}
                    onChange={(e) => setFormData({ ...formData, assunto: e.target.value })}
                    placeholder="Qual o assunto?"
                    required
                  />
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Mensagem
                    </label>
                    <textarea
                      value={formData.mensagem}
                      onChange={(e) => setFormData({ ...formData, mensagem: e.target.value })}
                      placeholder="Escreva sua mensagem..."
                      rows={5}
                      className="w-full px-4 py-3 rounded-xl bg-gray-800 border border-gray-700 text-white placeholder:text-gray-500 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none resize-none"
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    variant="nuvem"
                    className="w-full"
                    leftIcon={<Send className="w-4 h-4" />}
                  >
                    Enviar Mensagem
                  </Button>
                </form>
              )}
            </motion.div>

            {/* WhatsApp CTA */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="flex flex-col justify-center"
            >
              <div className="bg-gradient-to-br from-green-500/10 to-emerald-600/10 rounded-2xl p-8 border border-green-500/20">
                <div className="w-16 h-16 mb-6 rounded-2xl bg-green-500 flex items-center justify-center">
                  <MessageCircle className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-4">
                  Prefere falar no WhatsApp?
                </h2>
                <p className="text-gray-400 mb-6">
                  Nosso atendimento via WhatsApp é o canal mais rápido para tirar dúvidas 
                  e fazer orçamentos. Responderemos em poucos minutos!
                </p>
                <a
                  href="https://wa.me/5547997679994"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button
                    variant="nuvem"
                    className="w-full bg-green-500 hover:bg-green-600"
                    leftIcon={<MessageCircle className="w-5 h-5" />}
                  >
                    Iniciar Conversa
                  </Button>
                </a>
                <p className="text-center text-gray-500 text-sm mt-4">
                  Disponível de Segunda a Sexta, 9h às 18h
                </p>
              </div>

              {/* Social Links */}
              <div className="mt-8 flex justify-center gap-4">
                <a
                  href="#"
                  className="w-12 h-12 rounded-full bg-gray-800 flex items-center justify-center text-gray-400 hover:bg-pink-500 hover:text-white transition-colors"
                >
                  <Instagram className="w-5 h-5" />
                </a>
                <a
                  href="#"
                  className="w-12 h-12 rounded-full bg-gray-800 flex items-center justify-center text-gray-400 hover:bg-blue-500 hover:text-white transition-colors"
                >
                  <Facebook className="w-5 h-5" />
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-gray-900/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">
              Perguntas Frequentes
            </h2>
            <p className="text-gray-400">
              Tire suas dúvidas antes de entrar em contato
            </p>
          </div>

          <div className="space-y-4">
            {faq.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 rounded-xl bg-gray-800/50 border border-white/5"
              >
                <h3 className="text-white font-semibold mb-2">{item.question}</h3>
                <p className="text-gray-400 text-sm">{item.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
